/**
 * Authentication utility functions for Firebase
 * 
 * IMPORTANT: These functions should be used with the auth and db instances from FirebaseContext
 * rather than calling getAuth() or getFirestore() directly. This ensures consistent service
 * instance usage across the application, especially when dealing with RecaptchaVerifier and
 * other auth-related operations.
 * 
 * Example usage:
 * ```
 * import { useFirebase } from './components/FirebaseContext';
 * 
 * function MyComponent() {
 *   const { auth, db } = useFirebase();
 *   
 *   const handleLogin = async () => {
 *     await signInWithEmail(auth, db, email, password);
 *   };
 * }
 * ```
 */

import { 
  signInWithPopup, 
  GoogleAuthProvider, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  linkWithCredential,
  EmailAuthProvider,
  PhoneAuthProvider,
  fetchSignInMethodsForEmail,
  sendEmailVerification,
  RecaptchaVerifier,
  deleteUser,
  reauthenticateWithCredential
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  setDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  updateDoc,
  serverTimestamp,
  deleteDoc
} from 'firebase/firestore';

/**
 * Clamp handicap value to valid range (0.0 - 54.0 inclusive)
 * If handicap is out of range, automatically adjust to nearest valid value
 * @param {number|string} handicap - The handicap value to validate
 * @returns {number|undefined} The clamped handicap value, or undefined if invalid
 */
const clampHandicap = (handicap) => {
  if (handicap === undefined || handicap === null || handicap === '') {
    return undefined;
  }
  
  const numericHandicap = parseFloat(handicap);
  
  if (isNaN(numericHandicap)) {
    return undefined;
  }
  
  // Clamp to valid range 0.0 - 54.0 (inclusive)
  if (numericHandicap < 0.0) {
    console.warn(`Handicap ${numericHandicap} is below minimum, clamping to 0.0`);
    return 0.0;
  }
  
  if (numericHandicap > 54.0) {
    console.warn(`Handicap ${numericHandicap} is above maximum, clamping to 54.0`);
    return 54.0;
  }
  
  return numericHandicap;
};

/**
 * Check if a user with the given email already exists
 * @param {Object} auth - The Firebase Auth instance
 * @param {Object} db - The Firestore database instance
 * @param {string} email - The email to check
 * @returns {Promise<{exists: boolean, methods: string[], uid: string|null}>}
 */
export const checkExistingUser = async (auth, db, email) => {
  if (!email) return { exists: false, methods: [], uid: null };
  
  try {
    // Check if the email is already registered with any provider
    const methods = await fetchSignInMethodsForEmail(auth, email);
    
    if (methods.length > 0) {
      // Find the user document in Firestore by email
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("email", "==", email));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        return { exists: true, methods, uid: userDoc.id };
      }
      
      return { exists: true, methods, uid: null };
    }
    
    return { exists: false, methods: [], uid: null };
  } catch (error) {
    console.error("Error checking existing user:", error);
    return { exists: false, methods: [], uid: null, error };
  }
};

/**
 * Check if a user with the given phone number already exists
 * @param {Object} db - The Firestore database instance
 * @param {string} phoneNumber - The phone number to check
 * @returns {Promise<{exists: boolean, uid: string|null}>}
 */
export const checkExistingPhoneUser = async (db, phoneNumber) => {
  if (!phoneNumber) return { exists: false, uid: null };
  
  try {
    // Find the user document in Firestore by phone number
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("phoneNumber", "==", phoneNumber));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const userDoc = querySnapshot.docs[0];
      return { exists: true, uid: userDoc.id };
    }
    
    return { exists: false, uid: null };
  } catch (error) {
    console.error("Error checking existing phone user:", error);
    return { exists: false, uid: null, error };
  }
};

/**
 * Create or update a user document in Firestore
 * @param {Object} user - The Firebase Auth user object
 * @param {Object} db - The Firestore database instance
 * @param {Object} additionalData - Additional data to store in the user document
 * @returns {Promise<void>}
 */
export const createOrUpdateUserDocument = async (user, db, additionalData = {}) => {
  if (!user) return;
  
  const userRef = doc(db, "users", user.uid);
  
  // Apply handicap validation/clamping to additionalData
  const processedAdditionalData = { ...additionalData };
  if ('handicap' in processedAdditionalData) {
    const clampedHandicap = clampHandicap(processedAdditionalData.handicap);
    if (clampedHandicap !== undefined) {
      processedAdditionalData.handicap = clampedHandicap;
    } else {
      // Remove invalid handicap values
      delete processedAdditionalData.handicap;
    }
  }
  
  try {
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      // Update existing user document
      const userData = userDoc.data();
      
      // Apply handicap validation to existing data
      let existingHandicap = userData.handicap;
      if (existingHandicap !== undefined) {
        const clampedExistingHandicap = clampHandicap(existingHandicap);
        if (clampedExistingHandicap !== undefined && clampedExistingHandicap !== existingHandicap) {
          console.warn(`Existing handicap ${existingHandicap} was invalid, clamping to ${clampedExistingHandicap}`);
          existingHandicap = clampedExistingHandicap;
        }
      }
      
      const authProviders = userData.authProviders || [];
      
      // Get the provider ID from the current sign-in
      let providerId = user.providerData.length > 0 
        ? user.providerData[0].providerId 
        : 'password';
      
      // Add the provider if it's not already in the list
      if (!authProviders.includes(providerId)) {
        authProviders.push(providerId);
      }
      
      // Update the user document - preserve all existing fields
      await updateDoc(userRef, {
        ...userData, // Preserve all existing fields
        ...processedAdditionalData, // Apply new fields with validated handicap
        // Only set these fields if they're not already in processedAdditionalData
        email: processedAdditionalData.email !== undefined ? processedAdditionalData.email : (user.email || userData.email),
        phoneNumber: processedAdditionalData.phoneNumber !== undefined ? processedAdditionalData.phoneNumber : (user.phoneNumber || userData.phoneNumber || ''),
        username: processedAdditionalData.username !== undefined ? processedAdditionalData.username : (user.username || userData.username || ''),
        photoURL: processedAdditionalData.photoURL !== undefined ? processedAdditionalData.photoURL : (user.photoURL || userData.photoURL || ''),
        emailVerified: processedAdditionalData.emailVerified !== undefined ? processedAdditionalData.emailVerified : (user.emailVerified || userData.emailVerified || false),
        handicap: processedAdditionalData.handicap !== undefined ? processedAdditionalData.handicap : existingHandicap,
        authProviders,
        lastLogin: serverTimestamp()
      });
    } else {
      // Create new user document
      const newUserData = {
        uid: user.uid,
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        username: user.username || processedAdditionalData.username || '',
        photoURL: user.photoURL || '',
        emailVerified: user.emailVerified || false,
        phoneVerified: user.phoneNumber ? true : false,
        authProviders: user.providerData.length > 0 
          ? [user.providerData[0].providerId] 
          : ['password'],
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        ...processedAdditionalData
      };
      
      await setDoc(userRef, newUserData);
    }
  } catch (error) {
    console.error("Error creating/updating user document:", error);
    throw error;
  }
};

/**
 * Sign in with Google and handle account linking
 * @param {Object} auth - The Firebase Auth instance
 * @param {Object} db - The Firestore database instance
 * @returns {Promise<Object>} The user credential
 */
export const signInWithGoogle = async (auth, db) => {
  const provider = new GoogleAuthProvider();
  
  try {
    const result = await signInWithPopup(auth, provider);
    const user = result.user;
    
    // Create or update the user document
    await createOrUpdateUserDocument(user, db, {
      registrationMethod: "google"
    });
    
    return { user, credential: result.credential };
  } catch (error) {
    console.error("Error signing in with Google:", error);
    throw error;
  }
};

/**
 * Sign in with email and password, handling account linking
 * @param {Object} auth - The Firebase Auth instance
 * @param {Object} db - The Firestore database instance
 * @param {string} email - The user's email
 * @param {string} password - The user's password
 * @returns {Promise<Object>} The user credential
 */
export const signInWithEmail = async (auth, db, email, password) => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    const user = result.user;
    
    // Create or update the user document
    await createOrUpdateUserDocument(user, db, {
      registrationMethod: "email"
    });
    
    return { user };
  } catch (error) {
    console.error("Error signing in with email/password:", error);
    throw error;
  }
};

/**
 * Register a new user with email and password
 * @param {Object} auth - The Firebase Auth instance
 * @param {Object} db - The Firestore database instance
 * @param {string} email - The user's email
 * @param {string} password - The user's password
 * @returns {Promise<Object>} The user credential
 */
export const registerWithEmail = async (auth, db, email, password ) => {
  try {
    const { exists, methods } = await checkExistingUser(auth, db, email);
    
    if (exists) {
      throw new Error(`This email is already registered. Sign in methods: ${methods.join(', ')}`);
    }
    
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    await sendEmailVerification(user, {
      url: `${window.location.origin}/verify-email`,
      handleCodeInApp: true
    });
    
    await createOrUpdateUserDocument(user, db, {
      registrationMethod: "email",
      emailVerified: false
    });
    
    return { user };
  } catch (error) {
    console.error("Error registering with email/password:", error);
    throw error;
  }
};

/**
 * Link a phone number to an existing account
 * @param {Object} user - The Firebase Auth user object
 * @param {string} verificationId - The verification ID from the phone auth process
 * @param {string} verificationCode - The verification code entered by the user
 * @returns {Promise<Object>} The updated user
 */
export const linkPhoneNumber = async (user, verificationId, verificationCode) => {
  if (!user) throw new Error("User must be signed in to link a phone number");
  
  try {
    const phoneCredential = PhoneAuthProvider.credential(verificationId, verificationCode);
    await linkWithCredential(user, phoneCredential);
    await user.reload();
  } catch (error) {
    console.error("Error linking phone number:", error);
    throw error;
  }
};

/**
 * Link an email/password to an existing account
 * @param {Object} user - The Firebase Auth user object
 * @param {string} email - The email to link
 * @param {string} password - The password to set
 * @returns {Promise<Object>} The updated user
 */
export const linkEmailPassword = async (user, email, password) => {
  if (!user) throw new Error("User must be signed in to link email/password");
  
  try {
    const credential = EmailAuthProvider.credential(email, password);
    await linkWithCredential(user, credential);
    await user.reload();
  } catch (error) {
    console.error("Error linking email/password:", error);
    throw error;
  }
};

/**
 * Handle the case where a user tries to sign up with a new method
 * but the identifier (email or phone) is already linked to another account
 * @param {Object} auth - The Firebase Auth instance
 * @param {Object} db - The Firestore database instance
 * @param {string} email - The email address
 * @param {string} phoneNumber - The phone number
 * @returns {Promise<{action: string, existingUser: Object|null}>}
 */
export const handleExistingIdentifier = async (auth, db, email, phoneNumber) => {
  let existingUserByEmail = null;
  let existingUserByPhone = null;
  
  // Check if the email is already registered
  if (email) {
    const { exists: emailExists, uid: emailUid } = await checkExistingUser(auth, db, email);
    if (emailExists && emailUid) {
      const userRef = doc(db, "users", emailUid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        existingUserByEmail = userDoc.data();
      }
    }
  }
  
  // Check if the phone number is already registered
  if (phoneNumber) {
    const { exists: phoneExists, uid: phoneUid } = await checkExistingPhoneUser(db, phoneNumber);
    if (phoneExists && phoneUid) {
      const userRef = doc(db, "users", phoneUid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        existingUserByPhone = userDoc.data();
      }
    }
  }
  
  // If both email and phone are registered to different accounts
  if (existingUserByEmail && existingUserByPhone && existingUserByEmail.uid !== existingUserByPhone.uid) {
    return {
      action: "CONFLICT",
      message: "This email and phone number are registered to different accounts",
      existingUserByEmail,
      existingUserByPhone
    };
  }
  
  // If only email is registered
  if (existingUserByEmail) {
    return {
      action: "SIGN_IN_WITH_EMAIL",
      message: "This email is already registered. Please sign in with your existing account.",
      existingUser: existingUserByEmail
    };
  }
  
  // If only phone is registered
  if (existingUserByPhone) {
    return {
      action: "SIGN_IN_WITH_PHONE",
      message: "This phone number is already registered. Please sign in with your existing account.",
      existingUser: existingUserByPhone
    };
  }
  
  // No existing accounts found
  return {
    action: "PROCEED",
    message: "No existing accounts found with this email or phone number",
    existingUser: null
  };
};

/**
 * Get or create a RecaptchaVerifier instance
 * This function ensures only one RecaptchaVerifier instance exists per container
 * and handles proper cleanup of previous instances
 * 
 * @param {Object} auth - The Firebase Auth instance
 * @param {string} containerId - The ID of the container element for the reCAPTCHA
 * @param {Object} options - Options for the RecaptchaVerifier (optional)
 * @returns {Promise<RecaptchaVerifier>} The RecaptchaVerifier instance
 */
export const getRecaptchaVerifier = async (auth, containerId, options = {}) => {
  // For development environment, use Firebase's built-in testing mode
  if (process.env.NODE_ENV === 'development') {
    console.log('Setting up RecaptchaVerifier in development mode');
    
    // Enable testing mode in Firebase Auth
    auth.settings.appVerificationDisabledForTesting = true;
    
    // Create a container if it doesn't exist
    let container = document.getElementById(containerId);
    if (!container) {
      container = document.createElement('div');
      container.id = containerId;
      container.style.display = 'none';
      document.body.appendChild(container);
    }
    
    // Create a real RecaptchaVerifier but in testing mode
    const defaultOptions = {
      size: 'invisible',
      callback: () => console.log('reCAPTCHA verified in development mode')
    };
    
    const recaptchaVerifier = new RecaptchaVerifier(
      auth,
      containerId,
      { ...defaultOptions, ...options }
    );
    
    console.log('RecaptchaVerifier created in development mode');
    return recaptchaVerifier;
  }
  
  // For production environment, use the real RecaptchaVerifier
  // Create a global registry for RecaptchaVerifier instances if it doesn't exist
  if (!window.recaptchaRegistry) {
    window.recaptchaRegistry = {};
  }
  
  // Cleanup old RecaptchaVerifier instances (older than 10 minutes)
  const cleanupOldInstances = async () => {
    const now = new Date().getTime();
    const maxAge = 10 * 60 * 1000; // 10 minutes in milliseconds
    
    for (const id in window.recaptchaRegistry) {
      const entry = window.recaptchaRegistry[id];
      if (entry && (now - entry.createdAt > maxAge)) {
        try {
          // Try to clear the old verifier
          if (entry.verifier && typeof entry.verifier.clear === 'function') {
            await entry.verifier.clear();
          }
          
          // Remove the container if it still exists
          if (entry.container && entry.container.parentNode) {
            entry.container.parentNode.removeChild(entry.container);
          }
          
          // Delete the registry entry
          delete window.recaptchaRegistry[id];
          console.log(`Cleaned up old RecaptchaVerifier instance: ${id}`);
        } catch (error) {
          console.warn(`Error cleaning up old RecaptchaVerifier instance ${id}:`, error);
        }
      }
    }
  };
  
  // Run cleanup in the background
  cleanupOldInstances().catch(error => {
    console.warn('Error during RecaptchaVerifier cleanup:', error);
  });
  
  // Check if we already have a valid instance for this container
  if (window.recaptchaRegistry[containerId] && 
      window.recaptchaRegistry[containerId].verifier) {
    try {
      // Try to use the existing instance
      return window.recaptchaRegistry[containerId].verifier;
    } catch (error) {
      console.warn(`Error reusing existing RecaptchaVerifier for ${containerId}:`, error);
      // If there's an error, we'll create a new one
    }
  }
  
  // Create a permanent container for the reCAPTCHA if it doesn't exist
  let container = document.getElementById(containerId);
  if (!container) {
    // Create a new container element
    container = document.createElement('div');
    container.id = containerId;
    container.style.position = 'fixed';
    container.style.bottom = '0';
    container.style.right = '0';
    container.style.width = '300px';
    container.style.height = '100px';
    container.style.zIndex = '2147483647'; // Max z-index
    container.style.opacity = '0.01'; // Almost invisible but still present
    container.style.pointerEvents = 'none'; // Don't interfere with user interaction
    
    // Add a marker to identify this as a reCAPTCHA container
    container.setAttribute('data-recaptcha', 'true');
    
    // Add the container to the document body
    document.body.appendChild(container);
    
    console.log(`Created new reCAPTCHA container with ID: ${containerId}`);
  }
  
  // Clear any existing verifier for this container
  if (window.recaptchaRegistry[containerId] && 
      window.recaptchaRegistry[containerId].verifier) {
    try {
      await window.recaptchaRegistry[containerId].verifier.clear();
      console.log(`Cleared existing reCAPTCHA for ${containerId}`);
    } catch (error) {
      console.warn(`Error clearing existing reCAPTCHA for ${containerId}:`, error);
      // Continue even if clearing fails
    }
  }
  
  // Default options for RecaptchaVerifier
  const defaultOptions = {
    size: 'invisible',
    callback: () => {
      console.log(`reCAPTCHA for ${containerId} verified successfully`);
    },
    'expired-callback': () => {
      console.log(`reCAPTCHA for ${containerId} expired`);
    },
    'error-callback': (error) => {
      console.error(`reCAPTCHA for ${containerId} error:`, error);
    }
  };
  
  // Create a new RecaptchaVerifier instance
  console.log(`Creating new RecaptchaVerifier for ${containerId}`);
  const recaptchaVerifier = new RecaptchaVerifier(
    auth, 
    containerId, 
    { ...defaultOptions, ...options }
  );
  
  // Store the verifier in our registry with creation timestamp
  window.recaptchaRegistry[containerId] = {
    verifier: recaptchaVerifier,
    container: container,
    createdAt: new Date().getTime()
  };
  
  // Force render to ensure the reCAPTCHA is ready before use
  try {
    console.log(`Rendering RecaptchaVerifier for ${containerId}`);
    await recaptchaVerifier.render();
    console.log(`RecaptchaVerifier for ${containerId} rendered successfully`);
  } catch (error) {
    console.warn(`Error rendering RecaptchaVerifier for ${containerId}:`, error);
    
    // If it's already rendered, that's fine
    if (error.message && error.message.includes('has been rendered')) {
      console.log(`RecaptchaVerifier for ${containerId} was already rendered`);
    } else {
      // For other errors, try a fallback approach
      try {
        // Remove the problematic container
        if (container && container.parentNode) {
          container.parentNode.removeChild(container);
        }
        
        // Create a new container with a unique ID
        const fallbackId = `${containerId}-${Date.now()}`;
        const fallbackContainer = document.createElement('div');
        fallbackContainer.id = fallbackId;
        fallbackContainer.style.position = 'fixed';
        fallbackContainer.style.bottom = '0';
        fallbackContainer.style.right = '0';
        fallbackContainer.style.width = '300px';
        fallbackContainer.style.height = '100px';
        fallbackContainer.style.zIndex = '2147483647';
        fallbackContainer.style.opacity = '0.01';
        fallbackContainer.style.pointerEvents = 'none';
        document.body.appendChild(fallbackContainer);
        
        console.log(`Created fallback reCAPTCHA container with ID: ${fallbackId}`);
        
        // Create a new verifier with the fallback container
        const fallbackVerifier = new RecaptchaVerifier(
          auth, 
          fallbackId, 
          { ...defaultOptions, ...options }
        );
        
        // Render the fallback verifier
        await fallbackVerifier.render();
        
        // Store the fallback verifier in our registry
        window.recaptchaRegistry[fallbackId] = {
          verifier: fallbackVerifier,
          container: fallbackContainer,
          createdAt: new Date().getTime(),
          isFallback: true
        };
        
        console.log(`Fallback RecaptchaVerifier for ${fallbackId} rendered successfully`);
        
        // Return the fallback verifier
        return fallbackVerifier;
      } catch (fallbackError) {
        console.error(`Failed to create fallback RecaptchaVerifier:`, fallbackError);
        throw error; // Throw the original error
      }
    }
  }
  
  return recaptchaVerifier;
};

/**
 * Delete a user's account and associated data
 * @param {Object} user - The Firebase Auth user object
 * @param {Object} db - The Firestore database instance
 * @param {Object} userProfile - The user's profile data (for rollback)
 * @param {string} password - Optional password for reauthentication
 * @returns {Promise<void>}
 */
export const deleteUserAccount = async (user, db, userProfile, password = null) => {
  if (!user) throw new Error("User must be signed in to delete account");

  try {
    if (password && user.providerData?.some(provider => provider.providerId === 'password')) {
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
    }

    const userRef = doc(db, "users", user.uid);
    await deleteDoc(userRef);

    await deleteUser(user);
  } catch (error) {
    console.error("Error deleting user account:", error);

    // If Firestore delete succeeded but Auth delete failed, try to rollback
    if (error.code?.startsWith('auth/')) {
      try {
        const userRef = doc(db, "users", user.uid);
        await setDoc(userRef, {
          ...userProfile,
          updatedAt: serverTimestamp()
        });
      } catch (rollbackError) {
        console.error('Error rolling back Firestore deletion:', rollbackError);
      }
    }
    throw error;
  }
}; 